import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@components/Atoms/Box';
import { useAccountabilityExport } from './hooks';
import {
  AccountabilityFilters,
  Bucket,
  FilterOption,
  MetricsMode,
  TeachersMode,
} from './types';
import { UrlState } from './useUrlState';
import { DAYS_PER_BUCKET } from './intensity';
import {
  ControlCaption,
  ControlLabel,
  HelperText,
  SegmentButton,
  SegmentedGroup,
  ToggleButton,
} from './Accountability.style';

const BUCKETS: Array<{ value: Bucket; label: string }> = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const TEACHER_MODES: Array<{ value: TeachersMode; label: string }> = [
  { value: 'active', label: 'Active teachers' },
  { value: 'all', label: 'All' },
];

// One toolbar per page, so fixed ids are unique.
const SHOW_CAPTION_ID = 'accountability-show-caption';
const SHOW_HELP_ID = 'accountability-show-help';
const HIDE_UNUSED_HELP_ID = 'accountability-hide-unused-help';
const HIDE_EMPTY_HELP_ID = 'accountability-hide-empty-help';

interface ToolbarProps {
  state: UrlState;
  onChange: (patch: Partial<UrlState>) => void;
  /**
   * The bucket the server actually resolved for this window (`report.window.bucket`),
   * not `query.bucket ?? 'week'`. When the admin leaves the bucket unset and the
   * window exceeds 182 days, the server resolves 'month' (see windows.py's
   * `_auto_bucket`), and this control's label/default must match that, not
   * silently assume 'week' -- otherwise the label reads "per week" showing 5
   * while the legend (and the actual denominator used to colour every cell)
   * is "per month" showing 20, and a value typed here is applied against the
   * wrong denominator.
   */
  resolvedBucket: Bucket;
  /** Dropdown options; undefined until the filters endpoint answers. */
  filters?: AccountabilityFilters;
  filtersLoading?: boolean;
  filtersError?: boolean;
  /** How many activity types nobody in the view used. */
  unusedMetricCount: number;
  onOpenDefinitions: () => void;
}

const controlStyle: React.CSSProperties = {
  border: '1px solid #CBD0DA',
  borderRadius: 4,
  padding: '0.25rem 0.5rem',
  font: 'inherit',
  fontSize: '0.82rem',
};

const actionButtonStyle: React.CSSProperties = {
  ...controlStyle,
  color: '#2E5BC7',
  background: '#FFFFFF',
};

interface FilterSelectProps {
  label: string;
  allLabel: string;
  value: number | null;
  options: FilterOption[] | undefined;
  loading: boolean;
  onChange: (value: number | null) => void;
}

/** A native <select> over one of the filters endpoint's lists. */
const FilterSelect: React.FC<FilterSelectProps> = ({
  label,
  allLabel,
  value,
  options,
  loading,
  onChange,
}) => {
  const waiting = loading && !options;
  const known = options ?? [];
  // A shared link can carry an id the list lacks, for example when the options
  // failed to load. Show it as selected instead of silently showing "All".
  const unlisted = value !== null && !known.some((option) => option.id === value);

  let selected = '';
  if (!waiting && value !== null) {
    selected = String(value);
  }

  return (
    <ControlLabel>
      {label}
      <select
        style={controlStyle}
        value={selected}
        disabled={waiting}
        onChange={(event) =>
          onChange(event.target.value ? Number(event.target.value) : null)
        }
      >
        {waiting ? (
          <option value="">Loading…</option>
        ) : (
          <>
            <option value="">{allLabel}</option>
            {unlisted && <option value={String(value)}>Current selection</option>}
            {known.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </>
        )}
      </select>
    </ControlLabel>
  );
};

interface CheckboxToggleProps {
  label: string;
  checked: boolean;
  helpId: string;
  onChange: (checked: boolean) => void;
  /** The explanation shown under the checkbox. */
  children: React.ReactNode;
}

/** A view toggle with its explanation always visible underneath. */
const CheckboxToggle: React.FC<CheckboxToggleProps> = ({
  label,
  checked,
  helpId,
  onChange,
  children,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', pt: '1.1rem' }}>
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
      <input
        type="checkbox"
        checked={checked}
        aria-describedby={helpId}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
    <HelperText id={helpId}>{children}</HelperText>
  </Box>
);

const hideUnusedLabel = (mode: MetricsMode, count: number): string => {
  if (count === 0) {
    return 'Hide unused activity types';
  }
  return `Hide unused activity types (${count} ${mode === 'used' ? 'hidden' : 'unused'})`;
};

const Toolbar: React.FC<ToolbarProps> = ({
  state,
  onChange,
  resolvedBucket,
  filters,
  filtersLoading = false,
  filtersError = false,
  unusedMetricCount,
  onOpenDefinitions,
}) => {
  const { query } = state;
  const { exportCsv, isExporting, error: exportError } = useAccountabilityExport();

  // Press what the admin asked for, or what the server chose when they didn't.
  // The expected-days input follows `resolvedBucket`, the grain actually shown.
  const pressedBucket = query.bucket ?? resolvedBucket;
  const activeDayTargetValue = state.activeDayTarget ?? DAYS_PER_BUCKET[resolvedBucket];

  // Native date inputs need to own their edit buffer: without it, typing a
  // new date character-by-character races the controlled `value` prop,
  // which only advances once the parent commits the patch. Syncing the
  // draft from the prop on change keeps a pasted/shared URL authoritative.
  const [startDraft, setStartDraft] = React.useState(query.start);
  const [endDraft, setEndDraft] = React.useState(query.end);

  React.useEffect(() => {
    setStartDraft(query.start);
  }, [query.start]);

  React.useEffect(() => {
    setEndDraft(query.end);
  }, [query.end]);

  // Same edit-buffer problem as the date inputs above: a native number
  // input races the controlled `value` prop while the digits are typed one
  // at a time, so it needs its own draft, synced from the resolved
  // (override-or-default) value whenever that value actually changes.
  const [targetDraft, setTargetDraft] = React.useState(String(activeDayTargetValue));

  React.useEffect(() => {
    setTargetDraft(String(activeDayTargetValue));
  }, [activeDayTargetValue]);

  const rowSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    alignItems: 'flex-end',
  } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {/* What to report on. */}
      <Box sx={rowSx}>
        <ControlLabel>
          From
          <input
            type="date"
            style={controlStyle}
            value={startDraft}
            onChange={(event) => {
              setStartDraft(event.target.value);
              onChange({ query: { ...query, start: event.target.value } });
            }}
          />
        </ControlLabel>

        <ControlLabel>
          To
          <input
            type="date"
            style={controlStyle}
            value={endDraft}
            onChange={(event) => {
              setEndDraft(event.target.value);
              onChange({ query: { ...query, end: event.target.value } });
            }}
          />
        </ControlLabel>

        <SegmentedGroup role="group" aria-label="Time bucket">
          {BUCKETS.map((bucket) => (
            <SegmentButton
              key={bucket.value}
              type="button"
              aria-pressed={pressedBucket === bucket.value}
              onClick={() =>
                // "5" means something different per bucket, so an override
                // carried across a bucket switch would silently misdescribe
                // the new grain -- reset it back to "use the default".
                onChange({
                  query: { ...query, bucket: bucket.value },
                  activeDayTarget: null,
                })
              }
            >
              {bucket.label}
            </SegmentButton>
          ))}
        </SegmentedGroup>

        <FilterSelect
          label="School"
          allLabel="All schools"
          value={query.schoolIds[0] ?? null}
          options={filters?.schools}
          loading={filtersLoading}
          onChange={(id) =>
            onChange({ query: { ...query, schoolIds: id === null ? [] : [id] } })
          }
        />

        <FilterSelect
          label="Curriculum"
          allLabel="All curricula"
          value={query.curriculumId}
          options={filters?.curricula}
          loading={filtersLoading}
          onChange={(id) => onChange({ query: { ...query, curriculumId: id } })}
        />

        <FilterSelect
          label="Subject"
          allLabel="All subjects"
          value={query.subjectId}
          options={filters?.subjects}
          loading={filtersLoading}
          onChange={(id) => onChange({ query: { ...query, subjectId: id } })}
        />

        <ControlLabel>
          Find a teacher
          <input
            type="search"
            style={controlStyle}
            placeholder="Name"
            value={state.teacherSearch}
            onChange={(event) => onChange({ teacherSearch: event.target.value })}
          />
        </ControlLabel>

        {filtersError && <HelperText>Filter options could not be loaded.</HelperText>}
      </Box>

      {/* How to show it. */}
      <Box sx={{ ...rowSx, alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <ControlCaption id={SHOW_CAPTION_ID}>Show</ControlCaption>
          <SegmentedGroup
            role="group"
            aria-labelledby={SHOW_CAPTION_ID}
            aria-describedby={SHOW_HELP_ID}
          >
            {TEACHER_MODES.map((mode) => (
              <SegmentButton
                key={mode.value}
                type="button"
                aria-pressed={query.teachersMode === mode.value}
                onClick={() =>
                  onChange({ query: { ...query, teachersMode: mode.value } })
                }
              >
                {mode.label}
              </SegmentButton>
            ))}
          </SegmentedGroup>
          <HelperText id={SHOW_HELP_ID}>
            Active teachers hides teachers and classes with no active day in these dates.
            All adds archived classes, and shows inactive teachers while “Hide rows with
            no active days” is off.
          </HelperText>
        </Box>

        <CheckboxToggle
          label={hideUnusedLabel(state.metricsMode, unusedMetricCount)}
          checked={state.metricsMode === 'used'}
          helpId={HIDE_UNUSED_HELP_ID}
          onChange={(checked) => onChange({ metricsMode: checked ? 'used' : 'all' })}
        >
          Hides activity types nobody in this view used in these dates. A single
          teacher&rsquo;s zeros stay visible.
        </CheckboxToggle>

        <CheckboxToggle
          label="Hide rows with no active days"
          checked={state.emptyRows === 'hide'}
          helpId={HIDE_EMPTY_HELP_ID}
          onChange={(checked) => onChange({ emptyRows: checked ? 'hide' : 'show' })}
        >
          Hides schools, teachers and classes with no active day in these dates, even
          under All. When it is off, those rows are grayed out.
        </CheckboxToggle>

        <ControlLabel>
          {`Expected active days per ${resolvedBucket}`}
          <input
            type="number"
            min={1}
            max={31}
            step={1}
            style={controlStyle}
            value={targetDraft}
            onChange={(event) => {
              const raw = event.target.value;
              setTargetDraft(raw);
              // An empty box and a zero both look "falsy" but mean different
              // things: emptying the input must clear the override (fall
              // back to the bucket default), not coerce to 0.
              if (raw === '') {
                onChange({ activeDayTarget: null });
                return;
              }
              const parsed = Number(raw);
              onChange({ activeDayTarget: Number.isFinite(parsed) ? parsed : null });
            }}
          />
        </ControlLabel>

        <Box sx={{ flex: '1 1 auto' }} />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', pt: '1.1rem' }}>
          <ToggleButton
            type="button"
            aria-haspopup="dialog"
            onClick={onOpenDefinitions}
            style={actionButtonStyle}
          >
            <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
            Definitions
          </ToggleButton>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
            }}
          >
            <ToggleButton
              type="button"
              onClick={() => exportCsv(query)}
              disabled={isExporting}
              aria-busy={isExporting}
              style={{
                ...actionButtonStyle,
                cursor: isExporting ? 'default' : 'pointer',
                opacity: isExporting ? 0.6 : 1,
              }}
            >
              {isExporting ? 'Exporting…' : 'Export CSV'}
            </ToggleButton>
            {exportError && (
              <span role="alert" style={{ fontSize: '0.7rem', color: '#B3261E' }}>
                {exportError}
              </span>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Toolbar;
